// eslint-disable-next-line no-restricted-imports
import { gql, useMutation, useQuery } from "@apollo/client";
import { Grid } from "@material-ui/core";
import { useFlags } from "@resource/client-ffs";
import { CustomerPlanEnum } from "@resource/common";
import withPageAuthRequired from "auth/withPageAuthRequired";
import JourneyTable from "components/JourneyTable";
import Layout from "components/Layout";
import SYNC_ATS_JOBS from "components/legacy/RoleJourneys/SYNC_ATS_JOBS";
import ModalProvider from "components/ModalProvider";
import { useCurrentQueryParamStateWithDefaults } from "components/Table/hooks/useQueryParamState";
import FeatureFlags from "generated/FeatureFlags";
import {
  AllJobRoleGuideTemplateIds,
  AllJobRoleGuideTemplateIdsVariables,
  ForceIncrementalSyncOnMergeForJobs,
  GetOpenJobs,
  GetOpenJobsVariables,
  JobRoleGuideTemplatesFilterData,
  SyncATSJobsForJourneysDjangoAction,
} from "generated/schemaTypes";
import _ from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import { DefaultJourneyStatusFilter } from "utils/constants/defaults";
import { PermissionEnum } from "utils/permissions";

const FILTER_DATA = gql`
  query JobRoleGuideTemplatesFilterData {
    atsOffices {
      id
      name
    }
    atsDepartments {
      id
      name
    }
    atsUsers {
      id
      name
    }
  }
`;

const ALL_JOB_ROLE_GUIDE_TEMPLATE_IDS = gql`
  query AllJobRoleGuideTemplateIds(
    $search: String
    $atsUserIds: [String!]
    $atsOfficeIds: [String!]
    $atsDepartmentIds: [String!]
    $status: JourneyStatusEnum
  ) {
    currentOrganization {
      id
      jobRoleGuideTemplates(
        where: {
          isArchived: false
          search: $search
          atsUserIds: $atsUserIds
          atsOfficeIds: $atsOfficeIds
          atsDepartmentIds: $atsDepartmentIds
          status: $status
        }
      ) {
        id
        jobRole {
          id
        }
      }
    }
  }
`;

const FETCH_JOBS = gql`
  query GetOpenJobs(
    $search: String
    $atsUserIds: [String!]
    $atsOfficeIds: [String!]
    $atsDepartmentIds: [String!]
    $offset: Int
    $limit: Int
    $status: JourneyStatusEnum
    $orderBy: [OpenJobOrderByInput!]
  ) {
    currentUserPrisma {
      id
      currentOrganization {
        id
        customer {
          id
          atsIntegration {
            id
            atsType
          }
        }
      }
    }
    openJobs(
      where: {
        isArchived: false
        search: $search
        atsUserIds: $atsUserIds
        atsOfficeIds: $atsOfficeIds
        atsDepartmentIds: $atsDepartmentIds
        status: $status
      }
      offset: $offset
      limit: $limit
      orderBy: $orderBy
    ) {
      id
      name
      jobRole {
        id
        isDemo
        atsJob {
          id
          name
          requisitionId
          atsDepartments {
            id
            name
          }
          atsOffices {
            id
            name
          }
        }
      }
      updatedAt
      sentGuideCount
      configuredStageCount
      totalStageCount
      defaultGuideVersion
      unconfiguredStagesCount
      configuredStagesCount
      configuredStages {
        id
        name
        position
      }
      setupStatus
    }
    openJobsAggregate(
      where: {
        isArchived: false
        search: $search
        atsUserIds: $atsUserIds
        atsOfficeIds: $atsOfficeIds
        atsDepartmentIds: $atsDepartmentIds
        status: $status
      }
    ) {
      count
    }
  }
`;

const FORCE_INCREMENTAL_SYNC_ON_MERGE = gql`
  mutation ForceIncrementalSyncOnMergeForJobs {
    forceIncrementalSyncOnMerge {
      success
    }
  }
`;

function JourneysPage() {
  const {
    [FeatureFlags.SYNC_ATS_JOBS]: syncAtsJobsFlag,
    [FeatureFlags.MERGE_SYNC]: mergeSyncEnabled,
  } = useFlags();
  const {
    pageIndex,
    pageSize,
    filters,
    globalFilter: search,
    sortBy,
  } = useCurrentQueryParamStateWithDefaults();
  const initialVariables = {
    limit: pageSize,
    offset: pageIndex * pageSize,
    search,
    atsDepartmentIds: _.find(filters, { id: "department" })?.value ?? [],
    atsOfficeIds: _.find(filters, { id: "office" })?.value ?? [],
    atsUserIds: _.find(filters, { id: "user" })?.value ?? [],
    status:
      _.find(filters, { id: "status" })?.value ?? DefaultJourneyStatusFilter,
    orderBy: _(sortBy)
      // HACK: We need to remap this one id column to a different one on the
      // backend for sorting
      .map((sort) => ({
        ...sort,
        id: sort.id === "stageCount" ? "configuredStageCount" : sort.id,
      }))
      .map(({ id, desc }) => ({ [id]: desc ? "desc" : "asc" }))
      .value(),
  };
  const { data: filterData } =
    useQuery<JobRoleGuideTemplatesFilterData>(FILTER_DATA);

  const [triggerIncrementalSyncOnMerge] =
    useMutation<ForceIncrementalSyncOnMergeForJobs>(
      FORCE_INCREMENTAL_SYNC_ON_MERGE
    );

  const queryVariables = {
    ...initialVariables,
    atsUserIds: _(initialVariables.atsUserIds)
      .flatMap<string>((userIdsJsonArray: string) => {
        let value = "";
        try {
          value = JSON.parse(userIdsJsonArray);
        } catch (e) {
          console.warn("Invalid JSON value.");
        }
        return value;
      })
      .without("")
      .value(),
  };

  const { data, loading: isLoadingJourneys } = useQuery<
    GetOpenJobs,
    GetOpenJobsVariables
  >(FETCH_JOBS, {
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
  });

  const { data: allJobRoleGuideTemplateIdsData, refetch: refetchAllIds } =
    useQuery<AllJobRoleGuideTemplateIds, AllJobRoleGuideTemplateIdsVariables>(
      ALL_JOB_ROLE_GUIDE_TEMPLATE_IDS,
      {
        variables: queryVariables,
        fetchPolicy: "cache-and-network",
      }
    );

  const atsType =
    data?.currentUserPrisma?.currentOrganization?.customer.atsIntegration
      ?.atsType ?? "";

  const journeys = data?.openJobs ?? [];
  const atsUsers = useMemo(
    () =>
      _(filterData?.atsUsers)
        .groupBy("name")
        .mapValues((val) => ({
          id: JSON.stringify(_.map(val, "id")),
          label: val[0]?.name,
        }))
        .values()
        .value(),
    [filterData?.atsUsers]
  );

  const atsDepartments = useMemo(
    () =>
      _.map(filterData?.atsDepartments, ({ id, name }) => ({
        id,
        label: name,
      })),
    [filterData?.atsDepartments]
  );
  const atsOffices = useMemo(
    () =>
      _.map(filterData?.atsOffices, ({ id, name }) => ({ id, label: name })),
    [filterData?.atsOffices]
  );
  const totalCount = data?.openJobsAggregate.count;

  const allJobRoleGuideTemplateToJobIdsMap = _.zipObject(
    _.map(
      allJobRoleGuideTemplateIdsData?.currentOrganization
        ?.jobRoleGuideTemplates,
      "id"
    ),
    _.map(
      allJobRoleGuideTemplateIdsData?.currentOrganization
        ?.jobRoleGuideTemplates,
      "jobRole.id"
    )
  );

  const [syncATSJobs] = useMutation<SyncATSJobsForJourneysDjangoAction>(
    SYNC_ATS_JOBS,
    {
      refetchQueries: ["GetJobRoleGuideTemplates", "GetOpenJobs"],
    }
  );

  useEffect(() => {
    const sync = async () => {
      await syncATSJobs();
    };
    if (syncAtsJobsFlag) {
      sync();
    }
  }, [syncATSJobs, syncAtsJobsFlag]);

  useEffect(() => {
    if (mergeSyncEnabled) {
      triggerIncrementalSyncOnMerge();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetchIds = useCallback(
    async ({ search: newSearch }) => {
      refetchAllIds({
        search: newSearch,
      });
    },
    [refetchAllIds]
  );

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <p className="text-display-2">Jobs</p>
        </Grid>
        <Grid item xs={12}>
          <JourneyTable
            initialVariables={initialVariables}
            loading={isLoadingJourneys}
            journeys={journeys}
            allJourneyToJobIdsMap={allJobRoleGuideTemplateToJobIdsMap}
            refetchIds={refetchIds}
            offices={atsOffices}
            departments={atsDepartments}
            users={atsUsers}
            totalJourneysCount={totalCount}
            atsType={atsType}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default withPageAuthRequired(JourneysPage, {
  requiredPlan: CustomerPlanEnum.Platform,
  requiredPermissions: PermissionEnum.JOURNEY_WRITE,
  getLayout: (page) => (
    <ModalProvider>
      <Layout>{page}</Layout>
    </ModalProvider>
  ),
});
